/* ============================================================
   ProviderPricingPanel (TASK-179) — seller pricing settings:
   machine rates, buffers, margin, rush, minimum order,
   material price overrides, quantity discount tiers, job limits
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Material } from '../types';
import type {
  ProviderPricingSettings,
  ProviderPostProcessingOptions,
  QuantityDiscountTier,
} from '../types/pricing';
import { defaultPricingSettings, defaultPostProcessingOptions } from '../types/pricing';
import {
  MAX_POST_OPTIONS,
  postProcessingPayload,
  validatePostProcessingOptions,
} from './postProcessingView';

export function ProviderPricingPanel() {
  const [form, setForm] = useState<ProviderPricingSettings>(defaultPricingSettings);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await api.getPricingSettings();
      setForm(settings);
      setConfigured(true);
    } catch {
      // Not configured yet — start from safe defaults.
      setForm(defaultPricingSettings);
      setConfigured(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    api.getMaterials().then(setMaterials).catch(() => {});
  }, [load]);

  const set = <K extends keyof ProviderPricingSettings>(
    key: K,
    value: ProviderPricingSettings[K],
  ) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      // 00a0da87: client-side mirror of the backend post-processing rules —
      // block the same payloads the API would reject, and send null when
      // nothing is offered (never wipes an untouched section).
      const post = form.postProcessing ?? null;
      const postErrors = validatePostProcessingOptions(post ?? defaultPostProcessingOptions);
      const normalized = post == null ? null : postProcessingPayload(post);
      const postOk = post == null || (postErrors.length === 0 && normalized != null);
      if (!postOk) {
        setError(postErrors[0] ?? 'Проверьте настройки постобработки.');
        return;
      }
      const saved = await api.savePricingSettings({ ...form, postProcessing: normalized });
      setForm(saved);
      setConfigured(true);
      setMessage('Тарифы сохранены');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  // ── Material rule helpers ────────────────────────────────
  const toggleRule = (materialId: string, allowed: boolean) => {
    setForm((f) => {
      const rest = f.materialRules.filter((r) => r.materialId !== materialId);
      const existing = f.materialRules.find((r) => r.materialId === materialId);
      return {
        ...f,
        materialRules: [
          ...rest,
          {
            materialId,
            pricePerGramRub: existing?.pricePerGramRub ?? null,
            isAllowed: allowed,
          },
        ],
      };
    });
  };

  const setRulePrice = (materialId: string, price: string) => {
    const value = price === '' ? null : Number(price);
    setForm((f) => ({
      ...f,
      materialRules: f.materialRules.map((r) =>
        r.materialId === materialId ? { ...r, pricePerGramRub: value } : r,
      ),
    }));
  };

  // ── Tier helpers ─────────────────────────────────────────
  const addTier = () =>
    setForm((f) => ({
      ...f,
      discountTiers: [...f.discountTiers, { minCopies: f.discountTiers.length + 2, discountPercent: 5 }],
    }));

  const updateTier = (index: number, patch: Partial<QuantityDiscountTier>) =>
    setForm((f) => ({
      ...f,
      discountTiers: f.discountTiers.map((t, i) => (i === index ? { ...t, ...patch } : t)),
    }));

  const removeTier = (index: number) =>
    setForm((f) => ({ ...f, discountTiers: f.discountTiers.filter((_, i) => i !== index) }));

  // ── Post-processing helpers (00a0da87) ──────────────
  // null (not configured) edits from safe defaults — the payload stays
  // null until the seller actually offers something.
  const post: ProviderPostProcessingOptions = form.postProcessing ?? defaultPostProcessingOptions;

  const setPost = (patch: Partial<ProviderPostProcessingOptions>) =>
    setForm((f) => ({ ...f, postProcessing: { ...post, ...patch } }));

  const updateOption = (index: number, patch: Partial<{ name: string; pricePerCopyRub: number }>) =>
    setPost({ options: post.options.map((o, i) => (i === index ? { ...o, ...patch } : o)) });

  const removeOption = (index: number) =>
    setPost({ options: post.options.filter((_, i) => i !== index) });

  const addOption = () =>
    setPost({ options: [...post.options, { name: '', pricePerCopyRub: 0 }] });

  if (loading) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-lg)' }}>
      {message && <div className="badge badge-success">{message}</div>}
      {error && <div className="badge badge-danger">{error}</div>}
      {!configured && !error && (
        <div className="card" style={{ padding: 'var(--space-md)', fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          Тарифы ещё не настроены — заполните форму и сохраните. До этого расчёты идут по базовым значениям платформы.
        </div>
      )}

      {/* Machine time costs */}
      <div className="card" style={{ padding: 'var(--space-lg)', display: 'grid', gap: 'var(--space-md)' }}>
        <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Станок, ₽/час</h3>
        <div className="" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--space-sm)' }}>
          <NumberField label="Час работы принтера" value={form.machineHourlyRateRub} onChange={(v) => set('machineHourlyRateRub', v)} />
          <NumberField label="Амортизация" value={form.depreciationPerHourRub} onChange={(v) => set('depreciationPerHourRub', v)} />
          <NumberField label="Обслуживание" value={form.maintenancePerHourRub} onChange={(v) => set('maintenancePerHourRub', v)} />
          <NumberField label="Электричество" value={form.electricityPerHourRub} onChange={(v) => set('electricityPerHourRub', v)} />
        </div>
      </div>

      {/* Order costs & margin */}
      <div className="card" style={{ padding: 'var(--space-lg)', display: 'grid', gap: 'var(--space-md)' }}>
        <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Заказ и наценка</h3>
        <div className="" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--space-sm)' }}>
          <NumberField label="Подготовка, ₽" value={form.setupFeeRub} onChange={(v) => set('setupFeeRub', v)} />
          <NumberField label="Постобработка, ₽" value={form.laborPostProcessingRub} onChange={(v) => set('laborPostProcessingRub', v)} />
          <NumberField label="Брак/отходы, %" value={form.failureBufferPercent} onChange={(v) => set('failureBufferPercent', v)} />
          <NumberField label="Целевая маржа, %" value={form.targetMarginPercent} onChange={(v) => set('targetMarginPercent', v)} />
          <NumberField label="Надбавка за срочность, ×" value={form.rushMultiplier} step={0.1} onChange={(v) => set('rushMultiplier', v)} />
          <NumberField label="Минимальный заказ, ₽" value={form.minimumOrderRub} onChange={(v) => set('minimumOrderRub', v)} />
        </div>
      </div>

      {/* Material price overrides */}
      <div className="card" style={{ padding: 'var(--space-lg)', display: 'grid', gap: 'var(--space-md)' }}>
        <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Материалы</h3>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          Своя цена за грамм (иначе — цена каталога). Снимите галочку, чтобы не принимать материал.
        </p>
        <div style={{ display: 'grid', gap: 'var(--space-xs)' }}>
          {materials.map((m) => {
            const rule = form.materialRules.find((r) => r.materialId === m.id);
            return (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', minWidth: 160 }}>
                  <input
                    type="checkbox"
                    checked={rule ? rule.isAllowed : true}
                    onChange={(e) => toggleRule(m.id, e.target.checked)}
                  />
                  {m.name}
                </label>
                <input
                 
                  style={{ width: 120 }}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={`${m.pricePerGramRub} ₽/г`}
                  value={rule?.pricePerGramRub ?? ''}
                  onChange={(e) => setRulePrice(m.id, e.target.value)}
                />
                <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>₽/г</span>
              </div>
            );
          })}
          {materials.length === 0 && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>Каталог материалов пуст.</p>
          )}
        </div>
      </div>

      {/* Quantity discount tiers */}
      <div className="card" style={{ padding: 'var(--space-lg)', display: 'grid', gap: 'var(--space-md)' }}>
        <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Скидки за тираж</h3>
        <div style={{ display: 'grid', gap: 'var(--space-xs)' }}>
          {form.discountTiers.map((tier, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>от</span>
              <input
               
                style={{ width: 90 }}
                type="number"
                min="2"
                value={tier.minCopies}
                onChange={(e) => updateTier(i, { minCopies: Number(e.target.value) })}
              />
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>шт. —</span>
              <input
               
                style={{ width: 90 }}
                type="number"
                min="0"
                max="90"
                value={tier.discountPercent}
                onChange={(e) => updateTier(i, { discountPercent: Number(e.target.value) })}
              />
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>%</span>
              <button className="btn btn-secondary" onClick={() => removeTier(i)}>Удалить</button>
            </div>
          ))}
        </div>
        <button className="btn btn-secondary" style={{ justifySelf: 'start' }} onClick={addTier}>
          + Условие скидки
        </button>
      </div>

      {/* Post-processing offers (00a0da87) */}
      <div className="card" style={{ padding: 'var(--space-lg)', display: 'grid', gap: 'var(--space-md)' }}>
        <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Постобработка</h3>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
          Что вы предлагаете покупателям. Покраска считается по площади модели, удаление поддержек — по их весу;
          если метрика неизвестна, покупателю напишут «уточняется в чате».
        </p>

        <div style={{ display: 'grid', gap: 'var(--space-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', minWidth: 160 }}>
              <input
                type="checkbox"
                checked={post.offersPainting}
                onChange={(e) => setPost({ offersPainting: e.target.checked })}
              />
              Покраска
            </label>
            <input
              style={{ width: 120 }}
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={post.paintPricePerCm2Rub || ''}
              onChange={(e) => setPost({ paintPricePerCm2Rub: Number(e.target.value || 0) })}
            />
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>₽/см²</span>
            <input
              style={{ width: 110 }}
              type="number"
              min="0"
              step="1"
              placeholder="0"
              value={post.paintMinimumRub || ''}
              onChange={(e) => setPost({ paintMinimumRub: Number(e.target.value || 0) })}
            />
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>₽ минимум</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', minWidth: 160 }}>
              <input
                type="checkbox"
                checked={post.offersSupportRemoval}
                onChange={(e) => setPost({ offersSupportRemoval: e.target.checked })}
              />
              Удаление поддержек
            </label>
            <input
              style={{ width: 120 }}
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={post.supportRemovalPricePerGramRub || ''}
              onChange={(e) => setPost({ supportRemovalPricePerGramRub: Number(e.target.value || 0) })}
            />
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>₽/г</span>
            <input
              style={{ width: 110 }}
              type="number"
              min="0"
              step="1"
              placeholder="0"
              value={post.supportRemovalMinimumRub || ''}
              onChange={(e) => setPost({ supportRemovalMinimumRub: Number(e.target.value || 0) })}
            />
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>₽ минимум</span>
          </div>
        </div>

        <div style={{ display: 'grid', gap: 'var(--space-xs)' }}>
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
            Дополнительные опции с ценой за экземпляр — до {MAX_POST_OPTIONS}.
          </p>
          {post.options.map((o, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <input
                style={{ flex: '1 1 180px' }}
                type="text"
                maxLength={100}
                placeholder="Например: Шлифовка"
                value={o.name}
                onChange={(e) => updateOption(i, { name: e.target.value })}
              />
              <input
                style={{ width: 110 }}
                type="number"
                min="0"
                step="1"
                placeholder="0"
                value={o.pricePerCopyRub || ''}
                onChange={(e) => updateOption(i, { pricePerCopyRub: Number(e.target.value || 0) })}
              />
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>₽/шт</span>
              <button className="btn btn-secondary" onClick={() => removeOption(i)}>Удалить</button>
            </div>
          ))}
          <button
            className="btn btn-secondary"
            style={{ justifySelf: 'start' }}
            disabled={post.options.length >= MAX_POST_OPTIONS}
            onClick={addOption}
          >
            + Опция
          </button>
        </div>
      </div>

      {/* Job limits */}
      <div className="card" style={{ padding: 'var(--space-lg)', display: 'grid', gap: 'var(--space-md)' }}>
        <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Ограничения заказов</h3>
        <div className="" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--space-sm)' }}>
          <NumberField label="Мин. экземпляров" value={form.minCopies} onChange={(v) => set('minCopies', v)} />
          <NumberField label="Макс. экземпляров" value={form.maxCopies ?? 0} onChange={(v) => set('maxCopies', v || null)} />
          <NumberField label="Мин. вес, г" value={form.minWeightGrams} onChange={(v) => set('minWeightGrams', v)} />
          <NumberField label="Макс. вес, г" value={form.maxWeightGrams} onChange={(v) => set('maxWeightGrams', v)} />
          <NumberField label="Мин. габарит, мм" value={form.minDimensionMm} onChange={(v) => set('minDimensionMm', v)} />
          <NumberField label="Макс. габарит, мм" value={form.maxDimensionMm} onChange={(v) => set('maxDimensionMm', v)} />
          <NumberField label="Макс. время печати, мин" value={form.maxPrintMinutes} onChange={(v) => set('maxPrintMinutes', v)} />
        </div>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          0 — ограничение отключено.
        </p>
      </div>

      <div>
        <button className="btn btn-primary" disabled={saving} onClick={save}>
          {saving ? 'Сохранение…' : 'Сохранить тарифы'}
        </button>
      </div>
    </div>
  );
}

function NumberField({
  label, value, onChange, step = 1,
}: { label: string; value: number; onChange: (v: number) => void; step?: number }) {
  return (
    <label style={{ display: 'grid', gap: 'var(--space-xxs)', fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
      {label}
      <input
       
        type="number"
        step={step}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}
